import React, { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { Link } from 'react-router-dom';
import UpgradeButton from './components/UpgradeButton';
import env from './utils/env';

const SubscriptionManage: React.FC = () => {
    const { user, token, isFree, isUser } = useAuth();
    const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCreatingPortal, setIsCreatingPortal] = useState(false);
    
    const apiBaseUrl = env.apiUrl;

    useEffect(() => {
        const fetchSubscriptionStatus = async () => {
            if (!token) {
                setError('Authentication required');
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(`${apiBaseUrl}/subscription/status`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Failed to fetch subscription status');
                }

                setSubscriptionStatus(data);
            } catch (err: any) {
                console.error('Error fetching subscription status:', err);
                setError(err.message || 'Failed to fetch subscription status');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSubscriptionStatus();
    }, [token, apiBaseUrl]);

    const handleManageBilling = async () => {
        if (!token) {
            setError('Authentication required');
            return;
        }

        setIsCreatingPortal(true);
        setError(null);

        try {
            const response = await fetch(`${apiBaseUrl}/subscription/create-portal-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to create portal session');
            }

            // Redirect to Stripe customer portal
            window.location.href = data.portalUrl;
        } catch (err: any) {
            console.error('Error creating portal session:', err);
            setError(err.message || 'Failed to open billing portal');
        } finally {
            setIsCreatingPortal(false);
        }
    };

    if (isLoading) {
        return (
            <div className="container">
                <div className="manage-page">
                    <div className="loading-indicator">
                        <div className="spinner"></div>
                        <h2>Loading subscription details...</h2>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="manage-page">
                <div className="page-header">
                    <h1>Subscription Management</h1>
                    <p>Manage your subscription and billing information</p>
                </div>

                {error && (
                    <div className="error-message">
                        <span className="error-icon">⚠️</span>
                        {error}
                    </div>
                )}

                <div className="subscription-overview">
                    <div className="current-plan">
                        <h2>Current Plan</h2>
                        <div className="plan-details">
                            <div className="plan-name">
                                <span className={`plan-badge ${user?.role?.toLowerCase()}`}>
                                    {user?.role === 'FREE' ? 'Free' : user?.role === 'USER' ? 'Pro' : user?.role}
                                </span>
                            </div>
                            
                            {subscriptionStatus && (
                                <div className="subscription-info">
                                    <div className="info-item">
                                        <span className="label">Status:</span>
                                        <span className={`status ${subscriptionStatus.subscriptionStatus || 'free'}`}>
                                            {subscriptionStatus.subscriptionStatus || 'Free'}
                                        </span>
                                    </div>
                                    
                                    {subscriptionStatus.subscriptionCurrentPeriodEnd && (
                                        <div className="info-item">
                                            <span className="label">Next Billing Date:</span>
                                            <span className="value">
                                                {new Date(subscriptionStatus.subscriptionCurrentPeriodEnd).toLocaleDateString()}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {subscriptionStatus.subscriptionTrialEnd && (
                                        <div className="info-item">
                                            <span className="label">Trial Ends:</span>
                                            <span className="value">
                                                {new Date(subscriptionStatus.subscriptionTrialEnd).toLocaleDateString()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="plan-features">
                        <h3>Your Current Features</h3>
                        {isFree ? (
                            <ul className="features-list free">
                                <li>✅ Basic search functionality</li>
                                <li>✅ Limited daily searches</li>
                                <li>✅ Basic bond data access</li>
                                <li>❌ Advanced analytics</li>
                                <li>❌ Export capabilities</li>
                                <li>❌ Priority support</li>
                            </ul>
                        ) : isUser ? (
                            <ul className="features-list pro">
                                <li>✅ Unlimited searches</li>
                                <li>✅ Advanced analytics</li>
                                <li>✅ Full bond data access</li>
                                <li>✅ Export capabilities</li>
                                <li>✅ Priority support</li>
                                <li>✅ All premium features</li>
                            </ul>
                        ) : (
                            <ul className="features-list admin">
                                <li>✅ All Pro features</li>
                                <li>✅ User management</li>
                                <li>✅ Administrative controls</li>
                                <li>✅ System monitoring</li>
                            </ul>
                        )}
                    </div>
                </div>

                <div className="actions-section">
                    {isFree && (
                        <div className="upgrade-section">
                            <h3>Ready to Upgrade?</h3>
                            <p>Unlock all premium features with our Pro plan.</p>
                            <UpgradeButton size="large" variant="primary" showBenefits={false} />
                        </div>
                    )}

                    {isUser && subscriptionStatus?.hasActiveSubscription && (
                        <div className="billing-section">
                            <h3>Billing Management</h3>
                            <p>Update payment methods, view invoices, and manage your subscription.</p>
                            <button 
                                onClick={handleManageBilling}
                                disabled={isCreatingPortal}
                                className="billing-button"
                            >
                                {isCreatingPortal ? (
                                    <>
                                        <span className="spinner small"></span>
                                        Opening Billing Portal...
                                    </>
                                ) : (
                                    <>
                                        💳 Manage Billing
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    <div className="support-section">
                        <h3>Need Help?</h3>
                        <p>Our support team is here to help with any questions.</p>
                        <div className="support-options">
                            <a href="mailto:support@lularge.com" className="support-link">
                                📧 Email Support
                            </a>
                            <a href="https://help.lularge.com" target="_blank" rel="noopener noreferrer" className="support-link">
                                📚 Help Center
                            </a>
                        </div>
                    </div>
                </div>

                <div className="navigation-section">
                    <Link to="/content-search" className="nav-button primary">
                        Back to App
                    </Link>
                    <Link to="/bonds" className="nav-button secondary">
                        Browse Bonds
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionManage; 